import React from 'react';
import { Card } from './Card';
import { Badge } from './Badge';
import { Brain, TrendingUp, AlertTriangle, Search } from 'lucide-react';

export const SchemaAnalysisViewer = ({ analysisData }) => {
    if (!analysisData || !analysisData.analysis || Object.keys(analysisData.analysis).length === 0) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No schema analysis data available. Run analysis first.</p>
            </div>
        );
    }

    const {
        analysis
    } = analysisData.analysis;

    const tableClassifications = analysis?.table_classifications || [];
    const entities = analysis?.entities || [];
    const relationships = analysis?.relationships || [];
    const domainInsights = analysis?.domain_insights || [];
    const queryPatterns = analysis?.query_patterns || [];
    const potentialIssues = analysis?.potential_issues || [];
    const domain = analysis?.domain || '';

    const getClassificationBadgeColor = (classification) => {
        const colors = {
            'entity': 'bg-primary-100 text-primary-700',
            'transaction': 'bg-green-100 text-green-700',
            'lookup': 'bg-yellow-100 text-yellow-700',
            'audit': 'bg-primary-100 text-primary-700',
            'configuration': 'bg-gray-100 text-gray-700',
        };
        return colors[classification] || 'bg-gray-100 text-gray-700';
    };

    return (
        <div className="space-y-6">
            {/* Domain Overview */}
            {domain && (
                <div className="bg-gradient-to-r from-primary-50 to-primary-50 rounded-lg border border-primary-200 p-6">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Brain className="w-5 h-5 text-primary-600" />
                        Domain
                    </h3>
                    <p className="text-sm text-gray-700 capitalize">{domain}</p>
                </div>
            )}

            {/* Table Classifications */}
            {tableClassifications.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Table Classifications ({tableClassifications.length})</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {tableClassifications.map((classification, index) => (
                            <Card key={index} className="overflow-hidden">
                                <div className="p-4 border-b border-gray-200 bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                            <span className="bg-primary-100 text-primary-700 w-8 h-8 rounded-lg flex items-center justify-center text-sm">
                                                📊
                                            </span>
                                            {classification.table_name}
                                        </h4>
                                        <Badge className={`text-xs ${getClassificationBadgeColor(classification.classification)}`}>
                                            {classification.classification}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="mb-3">
                                        <p className="text-xs text-gray-600 mb-1">DOMAIN ENTITY</p>
                                        <p className="text-sm font-medium text-gray-900">{classification.domain_entity}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-600 mb-1">DESCRIPTION</p>
                                        <p className="text-sm text-gray-700">{classification.description}</p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Entities */}
            {entities.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Entities ({entities.length})</h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex flex-wrap gap-2">
                            {entities.map((entity, index) => (
                                <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                                    {entity}
                                </Badge>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Relationships */}
            {relationships.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Relationships ({relationships.length})</h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="space-y-3">
                            {relationships.map((relationship, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                                    <span className="text-primary-600 font-medium text-sm">{relationship}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Domain Insights */}
            {domainInsights.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Domain Insights ({domainInsights.length})
                    </h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="space-y-3">
                            {domainInsights.map((insight, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                                    <span className="text-green-800 text-sm">{insight}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Query Patterns */}
            {queryPatterns.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <Search className="w-5 h-5 text-primary-600" />
                        Query Patterns ({queryPatterns.length})
                    </h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="space-y-3">
                            {queryPatterns.map((pattern, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                                    <span className="text-primary-800 text-sm">{pattern}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Potential Issues */}
            {potentialIssues.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        Potential Issues ({potentialIssues.length})
                    </h3>
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="space-y-3">
                            {potentialIssues.map((issue, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                                    <span className="text-orange-800 text-sm">{issue}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchemaAnalysisViewer;
